/**
 * Every customer-facing string from the original static build, preserved
 * character for character. Do not "improve" these — `copy.test.ts` asserts them
 * against the legacy HTML and will fail CI on any drift.
 *
 * Strings for genuinely new surfaces (dashboards, team management, inventory)
 * live in `src/content/admin-copy.ts`.
 */
export const copy = {
  meta: {
    title: "Official Store — Nikita Kering'",
    description: "Official merchandise from Nikita Kering'. Pay with M-Pesa.",
    adminTitle: "Store admin — Nikita Kering'",
  },

  brand: {
    name: "Nikita Kering'",
    homeLabel: "Nikita Kering' home",
  },

  header: {
    cart: 'Cart',
    openCart: 'Open cart',
  },

  hero: {
    lines: ['The', 'Lick Back', 'Collection'],
    blurb: ["Limited pieces from Nikita Kering'.", 'Music. Culture. You.'],
    cta: 'Shop now',
    scroll: 'Scroll to the collection',
    imageAlt: "Nikita Kering' in a dramatic close-up",
  },

  catalog: {
    label: 'Official collection',
    title: 'Merch',
    loading: 'Loading…',
    empty: 'New merch is on the way. Check back soon.',
    unavailable: 'The store is unavailable right now. Please try again shortly.',
  },

  product: {
    close: 'Close',
    size: 'Size',
    selectSize: 'Select a size',
    addToCart: 'Add to cart',
    soldOut: 'Sold out',
    oneSize: 'One size',
    showPhoto: (index: number) => `Show photo ${index}`,
  },

  cart: {
    title: 'Your cart',
    close: 'Close cart',
    total: 'Total',
    empty: 'Your cart is empty.',
    oneLess: (name: string) => `One less ${name}`,
    oneMore: (name: string) => `One more ${name}`,
    remove: (name: string) => `Remove ${name}`,
    continue: 'Continue shopping',
    thankYou: 'Thank you',
    order: 'Order',
  },

  checkout: {
    name: 'Full name',
    phone: 'M-Pesa phone number',
    phonePlaceholder: '07XX XXX XXX',
    email: 'Email (optional)',
    address: 'Delivery town and address',
    pay: 'Pay with M-Pesa',
    placing: 'Placing your order…',
    emptyCart: 'Your cart is empty.',
    genericError: 'Something went wrong. Please try again.',
    networkError: 'Network problem. Check your connection and try again.',
    invalid: 'Please check your name, phone number and delivery address.',
    prompt: 'Check your phone for the M-Pesa prompt.',
  },

  payment: {
    receivedTitle: 'Payment received',
    receivedText: 'Thank you! Your order is confirmed.',
    failedTitle: 'Payment not completed',
    failedText: 'The payment did not go through. You can close this and try again.',
    waitingTitle: 'Still waiting',
    waitingText: 'We have not received your payment yet. If you have paid, your order will be confirmed shortly.',
  },

  footer: {
    rights: "© 2026 Nikita Kering'. All rights reserved.",
    officialSite: 'Official website',
    support: 'Contact support',
  },

  admin: {
    title: 'Store admin',
    password: 'Password',
    login: 'Log in',
    products: 'Products',
    orders: 'Orders',
    viewShop: 'View shop',
    logout: 'Log out',
    addProduct: 'Add a product',
    editProduct: 'Edit product',
    photosCaption: 'Photos (up to 4). The first one is the cover; click a photo to make it the cover.',
    name: 'Name',
    price: 'Price in KES',
    sizes: 'Sizes (leave empty for one size)',
    description: 'Description (optional)',
    soldOut: 'Sold out',
    add: 'Add product',
    save: 'Save changes',
    cancel: 'Cancel',
    checkPayments: 'Check payments',
    noProducts: 'No products yet. Add your first one.',
    noOrders: 'No orders yet.',
    status: 'Status',
    edit: 'Edit',
    remove: 'Remove',
    cover: 'Cover',
    addAnother: 'Add another',
    dropPhotos: 'Drop photos here or click to choose',
    photo: (index: number) => `Photo ${index}`,
    removePhoto: (index: number) => `Remove photo ${index}`,
    coverTitle: 'Cover photo',
    makeCover: 'Click to make this the cover photo',
    loginChecking: 'Checking…',
    pleaseLogin: 'Please log in again.',
    preparingPhotos: 'Preparing photos…',
    uploadingPhotos: 'Uploading photos…',
    saving: 'Saving…',
    saved: 'Saved.',
    added: 'Added. It will show in the shop within a few seconds.',
    needPhoto: 'Add at least one photo.',
    photoLimit: (max: number) => `You can add up to ${max} photos.`,
    photoTypes: 'Choose PNG, JPG or WebP photos under 25 MB.',
    photoSkipped: (max: number) => `Only ${max} photos fit, so the rest were skipped.`,
    checkingPayments: 'Checking payments…',
    paymentsNotConnected: 'Payments are not connected yet.',
    nothingToCheck: 'Nothing is waiting to be checked.',
    checked: (checked: number, paid: number, failed: number) =>
      `Checked ${checked} waiting order${checked === 1 ? '' : 's'}: ${paid} paid, ${failed} not completed.`,
    confirmRemove: (name: string) => `Remove "${name}" from the shop?`,
  },

  // Added in the rebuild — the legacy site had no self-service password change.
  account: {
    changeTitle: 'Choose a new password',
    changeIntro:
      'Your account was created by invite, so the first password was chosen by whoever opened the email. Set your own to continue.',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordHint: 'At least 10 characters.',
    savePassword: 'Save password',
    saving: 'Saving…',
    mismatch: 'Those passwords do not match.',
    tooShort: 'Use at least 10 characters.',
    changeFailed: 'Could not update the password. Please try again.',
    signedInAs: (email: string) => `Signed in as ${email}`,
  },

  status: {
    pending: 'Awaiting payment',
    paid: 'Paid',
    fulfilled: 'Fulfilled',
    cancelled: 'Cancelled',
    failed: 'Payment failed',
    refunded: 'Refunded',
  } as const,
} as const;

export type Copy = typeof copy;
